"use client";
import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMe, useLogout } from "../api/api_hooks";
import RemotePairingButton from "../remote/RemotePairingButton";
import { getSurveyContent } from "../survey/surveyContent";

function NavIcon({ name, className = "h-[18px] w-[18px]" }) {
  const paths = {
    games: <><path d="M8.25 6.75h7.5a5.25 5.25 0 0 1 5.07 3.88l1.06 3.9a2.83 2.83 0 0 1-4.66 2.82l-1.74-1.6H8.52l-1.74 1.6a2.83 2.83 0 0 1-4.66-2.82l1.06-3.9a5.25 5.25 0 0 1 5.07-3.88Z" /><path d="M7.5 10.5v3m-1.5-1.5h3m7.5-.75h.01m2.24 2.25h.01" /></>,
    tournaments: <><path d="M8.25 21h7.5M12 17.25V21m5.25-18v4.5A5.25 5.25 0 0 1 12 12.75 5.25 5.25 0 0 1 6.75 7.5V3.25h10.5Z" /><path d="M6.75 5.25H3.5v1.5a3 3 0 0 0 3 3m10.75-4.5h3.25v1.5a3 3 0 0 1-3 3" /></>,
    plans: <><path d="M3.75 7.5A2.25 2.25 0 0 1 6 5.25h12a2.25 2.25 0 0 1 2.25 2.25v9A2.25 2.25 0 0 1 18 18.75H6a2.25 2.25 0 0 1-2.25-2.25v-9Z" /><path d="M3.75 9.75h16.5M7.5 15h3" /></>,
    survey: <><path d="M7 3.75h10A2.25 2.25 0 0 1 19.25 6v14.25H4.75V6A2.25 2.25 0 0 1 7 3.75Z" /><path d="M8.5 8.25h7m-7 4h7m-7 4h4" /><path d="M9 3.75v-1h6v1" /></>,
    about: <><circle cx="12" cy="12" r="9" /><path d="M12 10.5v6m0-9.75h.01" /></>,
    account: <><circle cx="12" cy="8.25" r="3.25" /><path d="M5.75 20a6.25 6.25 0 0 1 12.5 0" /></>,
    login: <><path d="M14.25 8.25 18 12l-3.75 3.75M18 12H6.75" /><path d="M10.5 4.5H5.25A2.25 2.25 0 0 0 3 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25h5.25" /></>,
    signup: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0m4-11v6m-3-3h6" /></>,
    logout: <><path d="m15.75 8.25 3.75 3.75-3.75 3.75M19.5 12H8.25" /><path d="M12 4.5H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25H12" /></>,
    menu: <path d="M4 7h16M4 12h16M4 17h16" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
  };

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

const guestLinks = [
  { to: "/", label: "Games", icon: "games", end: true },
  { to: "/subscriptions", label: "Plans", icon: "plans" },
  { to: "/about", label: "About", icon: "about" },
];

function HeaderLink({ item, mobile = false }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => `${mobile ? "w-full justify-start px-4 py-3" : "px-3 py-2.5"} group relative flex items-center gap-2 rounded-xl text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${isActive ? "bg-white/10 text-white shadow-inner ring-1 ring-white/10" : "text-indigo-100/70 hover:bg-white/[0.06] hover:text-white"}`}
    >
      {({ isActive }) => (
        <>
          <NavIcon name={item.icon} className={`h-[18px] w-[18px] transition-colors ${isActive ? "text-pink-300" : "text-indigo-200/60 group-hover:text-indigo-100"}`} />
          <span>{item.label}</span>
          {isActive && !mobile && <span className="absolute inset-x-3 -bottom-[13px] h-0.5 rounded-full bg-gradient-to-r from-violet-400 to-pink-400 shadow-[0_0_10px_rgba(244,114,182,0.8)]" />}
        </>
      )}
    </NavLink>
  );
}

export default function Header() {
  const surveyContent = getSurveyContent();
  const { data: me, isLoading, isFetching } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => setMenuOpen(false), [location.pathname]);

  const onLogout = () => {
    if (logout.isPending) return;
    logout.mutate(undefined, { onSuccess: () => navigate("/") });
  };

  const loading = isLoading || (isFetching && !me);
  const signedInLinks = [
    { to: "/", label: "Games", icon: "games", end: true },
    { to: "/tournaments", label: "Tournaments", icon: "tournaments" },
    { to: "/survey", label: surveyContent.navLabel, icon: "survey" },
    { to: "/subscriptions", label: "Plans", icon: "plans" },
    { to: "/about", label: "About", icon: "about" },
  ];
  const links = me ? signedInLinks : guestLinks;
  const playerName = me?.username || me?.email?.split("@")[0] || "Player";
  const playerInitial = playerName.charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-[1100] w-full border-b border-white/10 bg-indigo-600/30 shadow-lg shadow-slate-950/20 backdrop-blur-2xl">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-pink-400/70 to-transparent" />
      <div className="mx-auto flex min-h-[76px] w-full max-w-[1600px] items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="group flex shrink-0 items-center gap-3 rounded-xl text-inherit no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300" aria-label="Pocket Play home">
          <div className="relative">
            <div className="absolute inset-1 rounded-2xl bg-cyan-400/30 blur-md transition group-hover:bg-pink-400/30" />
            <img className="relative h-12 w-12 rounded-2xl bg-indigo-500/10 object-cover shadow-lg ring-1 ring-cyan-300/40 transition duration-300 group-hover:-rotate-3 group-hover:scale-105 group-hover:ring-pink-300/60 sm:h-14 sm:w-14" src="/logo.png" alt="" />
          </div>
          <div className="text-left">
            <div className="text-sm font-black uppercase tracking-[0.26em] text-indigo-100 sm:text-base">Pocket Play</div>
            <div className="mt-0.5 hidden text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-300/50 sm:block">Play together</div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
          {loading ? (
            <div className="flex items-center gap-2 px-4 text-sm text-indigo-100/50"><span className="h-2 w-2 animate-pulse rounded-full bg-violet-400" />Loading</div>
          ) : links.map((item) => <HeaderLink key={item.to} item={item} />)}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <RemotePairingButton />
          {!loading && me ? (
            <>
              <NavLink to="/account" className={({ isActive }) => `group flex items-center gap-3 rounded-2xl p-1.5 pr-3 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${isActive ? "bg-white/10 ring-1 ring-white/10" : "hover:bg-white/[0.06]"}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-sm font-black text-white shadow-lg shadow-fuchsia-950/40">{playerInitial}</span>
                <span className="max-w-32 text-left">
                  <span className="block truncate text-xs font-bold text-white">{playerName}</span>
                  <span className="block truncate text-[10px] text-indigo-200/50">View account</span>
                </span>
              </NavLink>
              <button type="button" onClick={onLogout} disabled={logout.isPending} className="group flex h-10 w-10 items-center justify-center rounded-xl text-pink-300/80 transition hover:bg-pink-400/10 hover:text-pink-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 disabled:opacity-40" aria-label={logout.isPending ? "Logging out" : "Log out"} title="Log out">
                <NavIcon name="logout" />
              </button>
            </>
          ) : !loading && (
            <>
              <Link to="/login" className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-indigo-100/75 transition hover:bg-white/[0.06] hover:text-white"><NavIcon name="login" />Log in</Link>
              <Link to="/register" className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-fuchsia-950/30 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300"><NavIcon name="signup" />Sign up</Link>
            </>
          )}
        </div>

        <button type="button" onClick={() => setMenuOpen((open) => !open)} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-indigo-100 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 lg:hidden" aria-expanded={menuOpen} aria-controls="mobile-navigation" aria-label={menuOpen ? "Close navigation" : "Open navigation"}>
          <NavIcon name={menuOpen ? "close" : "menu"} className="h-6 w-6" />
        </button>
      </div>

      {menuOpen && (
        <nav id="mobile-navigation" className="border-t border-white/10 bg-slate-950/95 px-4 pb-5 pt-3 shadow-2xl backdrop-blur-2xl lg:hidden" aria-label="Mobile navigation">
          <div className="mx-auto flex max-w-xl flex-col gap-1">
            <RemotePairingButton mobile />
            {loading ? <div className="px-4 py-3 text-sm text-indigo-100/50">Loading...</div> : links.map((item) => <HeaderLink key={item.to} item={item} mobile />)}
            {!loading && me ? (
              <>
                <HeaderLink item={{ to: "/account", label: "Account", icon: "account" }} mobile />
                <button type="button" onClick={onLogout} disabled={logout.isPending} className="mt-2 flex w-full items-center gap-2 rounded-xl border border-pink-400/15 bg-pink-400/[0.06] px-4 py-3 text-left text-sm font-semibold text-pink-200 transition hover:bg-pink-400/10 disabled:opacity-40"><NavIcon name="logout" />{logout.isPending ? "Logging out..." : "Log out"}</button>
              </>
            ) : !loading && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <HeaderLink item={{ to: "/login", label: "Log in", icon: "login" }} mobile />
                <HeaderLink item={{ to: "/register", label: "Sign up", icon: "signup" }} mobile />
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
